// Health and Search Readiness wherever a requisition is listed (lane B10, slice S19; plan D13; CR-046 "Health = numeric
// + traffic light + drivers."; CR-095 "Search Readiness percent with missing prerequisites; posting is not blocked by
// pending Compensation."). One batched `requisition.signals` read answers a whole list (≤ 100 ids a call, the tool's
// own bound); each row draws D0's Health `sm` (the score in the ring, the band word, the drivers counted in its
// accessible name — DC-17), the Search Readiness ring with its percent, and the missing-prerequisite hint: *Missing
// prerequisites* with the count, deep-linked to the screen that clears the first one (DC-50, DC-22). The `md` form adds
// each missing prerequisite as a deep-linked check row and the posting sentence. Signals are B1's snapshots read as they
// are: a requisition whose snapshot is not computed yet shows "—", never 0, and a requisition the reader may not open
// is simply absent from the answer, so its row shows "—" with no count. A prerequisite no lane records yet ("fed":
// false) is not yet available, never missing. Rows name roles and items only; nothing here scores a person ("Health
// informs attention; it does not make employment decisions.").
//
// Provided to the other B10 screens (PARALLEL-MAP §2.1 S19): <RowSignals signal size hs />, useSignals(ids) and
// useSignalsQuery(ids). B2's Requisitions list takes the read and the row through its slots (./register.ts, C-B10-13).

import { useId, useMemo } from "react";
import { requisitionSignals, type HealthOut, type ReadinessOut, type SignalRow } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  CheckRow, Checklist, Count, Flex, Health, METRIC_LABELS, ROLE_LABELS, Ring, SURFACE_LABELS, SectionTitle, Skeleton, Stack,
  TextLink, VisuallyHidden, cx, type HealthDriver, type SeatKey,
} from "@/mc";
import { LIST_LABELS } from "./labels";

export type { SignalRow };

/** The most ids one `requisition.signals` call takes (its input's `max_length`). */
export const SIGNALS_BATCH = 100;

/** The prerequisite B1 declares for the Compensation review: pending, it never blocks posting (BRD §4.4). */
export const COMPENSATION_REVIEW = "compensation_review";

/** The column head of the V1 lists: his two words, the band's and the ring's. */
export const SIGNALS_COLUMN = `${METRIC_LABELS.reqHealth} · ${METRIC_LABELS.searchReadiness}`;

const NONE = "—";

/** One prerequisite as B1 records it on the readiness snapshot ({key, owner_role, fed, met, route, blocking_posting}). */
export interface Prerequisite {
  key: string;
  ownerRole: string | null;
  fed: boolean | null;
  met: boolean | null;
  route: string | null;
}

/** The eight roles' keys (backend `MATRIX_ROLES`) to the seats whose words D0 carries (G §3). */
const ROLE_SEAT: Readonly<Record<string, SeatKey>> = {
  hm: "hm",
  recruiter: "recruiter",
  coordinator: "coordinator",
  hrbp: "hrbp",
  compensation: "compensation",
  interviewer: "interviewer",
  ta_manager: "taManager",
  admin: "admin",
};

/** His word for a role key, or undefined for a key that is not one of the eight. */
export function roleWord(role: string | null | undefined): string | undefined {
  const seat = role ? ROLE_SEAT[role] : undefined;
  return seat ? ROLE_LABELS[seat] : undefined;
}

const text = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);
const flag = (value: unknown): boolean | null => (typeof value === "boolean" ? value : null);

/** A snapshot row read defensively: anything without a key is not a prerequisite. */
export function asPrerequisite(raw: Record<string, unknown>): Prerequisite | null {
  const key = text(raw["key"]);
  if (key === null) return null;
  return { key, ownerRole: text(raw["owner_role"]), fed: flag(raw["fed"]), met: flag(raw["met"]), route: text(raw["route"]) };
}

/**
 * The prerequisites still to meet, in B1's order: recorded and not met. One that no lane records yet (`fed: false`,
 * `met: null`) is "not yet available" and is not missing; B1's percent is over the recorded ones only.
 */
export function missingPrerequisites(readiness: ReadinessOut | undefined): Prerequisite[] {
  if (!readiness || readiness.status !== "available") return [];
  const rows = readiness.missing ?? readiness.prerequisites ?? [];
  return rows
    .map(asPrerequisite)
    .filter((p): p is Prerequisite => p !== null && p.met === false && p.fed !== false);
}

/** True while the Compensation review is among the missing: the row then says posting is not blocked by it. */
export function compensationPending(missing: readonly Prerequisite[]): boolean {
  return missing.some((p) => p.key === COMPENSATION_REVIEW);
}

/**
 * The drivers that took points off the score (weight > 0), in their order, for Health's accessible name ("Req health
 * 72, Yellow: 3 drivers"). A driver at weight 0 is shown in the full Health (S16), not counted on a row.
 */
export function healthDrivers(health: HealthOut | undefined): HealthDriver[] {
  if (!health || health.status !== "available") return [];
  return (health.drivers ?? [])
    .filter((d) => d.weight > 0)
    .map((d) => ({ status: "warn" as const, text: d.label_key ?? d.key, href: d.route ?? undefined }));
}

/** The score and band to draw, or null when the snapshot is not computed yet ("not yet available", never 0). */
export function healthBand(health: HealthOut | undefined): { score: number; band: "green" | "yellow" | "red" } | null {
  if (!health || health.status !== "available") return null;
  if (typeof health.score !== "number" || !health.light) return null;
  return { score: health.score, band: health.light };
}

/** The readiness percent to draw, or null when the snapshot is not computed or nothing is recorded yet. */
export function readinessPercent(readiness: ReadinessOut | undefined): number | null {
  if (!readiness || readiness.status !== "available") return null;
  return typeof readiness.percent === "number" ? readiness.percent : null;
}

import { hsKey } from "@/views/b10/lib/hsKey";

export { hsKey };

export interface RowSignalsProps {
  /** The requisition's row from `requisition.signals`; absent: the reader may not open it, or it is still loading. */
  signal?: SignalRow | undefined;
  /** `sm` (default) is one line for a list row; `md` adds the missing prerequisites as rows (Home cards, drill rows). */
  size?: "sm" | "md" | undefined;
  /** Hotspot prefix (default `dash.signals.<requisition id>`), held to the capture grammar like the default. */
  hs?: string | undefined;
  /** The batch is in flight: skeletons at the final geometry (DC-25). */
  loading?: boolean | undefined;
  /** false where the row is itself a link (a board card): the hint is words, never a link inside a link. */
  links?: boolean | undefined;
  className?: string | undefined;
}

/** A figure the snapshot does not hold yet: his word for assistive technology, "—" on the screen. */
function NotYet({ word }: { word: string }) {
  return (
    <span className="mc-t3">
      <VisuallyHidden>{word}</VisuallyHidden>
      {NONE}
    </span>
  );
}

export function RowSignals({ signal, size = "sm", hs, loading, links = true, className }: RowSignalsProps) {
  const titleId = `${useId()}-missing`;
  const md = size === "md";
  const ring = md ? 44 : 28;
  if (!signal) {
    if (loading) {
      return (
        <Flex gap={8} wrap className={cx("mc-row-signals", className)}>
          <Health size="sm" score={0} band="green" loading />
          <span aria-busy="true"><Skeleton width={ring} height={ring} shape="pill" /></span>
        </Flex>
      );
    }
    return <span className={cx("mc-row-signals", "mc-t3", className)}>{NONE}</span>;
  }
  const key = hs !== undefined ? hsKey(hs) : hsKey("dash.signals", signal.requisition_id);
  const band = healthBand(signal.health);
  const pct = readinessPercent(signal.readiness);
  const missing = missingPrerequisites(signal.readiness);
  const first = missing.find((p) => p.route !== null);
  const posting = compensationPending(missing);
  const health = band
    ? <Health size="sm" score={band.score} band={band.band} drivers={healthDrivers(signal.health)} />
    : <NotYet word={METRIC_LABELS.reqHealth} />;
  const readiness = pct !== null
    ? <Ring size={ring} pct={pct} label={`${METRIC_LABELS.searchReadiness} ${pct}%`} hs={hsKey(key, "readiness")} />
    : <NotYet word={METRIC_LABELS.searchReadiness} />;
  const hint = missing.length > 0 ? (
    <span className="mc-row-signals-hint">
      {links && first?.route
        ? <TextLink href={first.route} hs={hsKey(key, "missing")}>{SURFACE_LABELS.missingPrerequisites}</TextLink>
        : <span className="mc-t2">{SURFACE_LABELS.missingPrerequisites}</span>}
      {" "}
      <Count n={missing.length} />
      {posting && !md ? <VisuallyHidden>{LIST_LABELS.postingNotBlocked}</VisuallyHidden> : null}
    </span>
  ) : null;
  if (!md) {
    return (
      <Flex gap={8} wrap className={cx("mc-row-signals", className)}>
        {health}
        {readiness}
        {hint}
      </Flex>
    );
  }
  return (
    <Stack gap={8} className={cx("mc-row-signals", "mc-row-signals-md", className)}>
      <Flex gap={10} wrap>
        {health}
        {readiness}
      </Flex>
      {missing.length > 0 ? (
        <>
          <SectionTitle id={titleId}>{SURFACE_LABELS.missingPrerequisites}</SectionTitle>
          <Checklist labelledBy={titleId}>
            {missing.map((p) => (
              <CheckRow
                key={p.key}
                status="todo"
                href={links && p.route ? p.route : undefined}
                hs={links && p.route ? hsKey(key, "missing", p.key) : undefined}
              >
                {roleWord(p.ownerRole) ?? p.key}
              </CheckRow>
            ))}
          </Checklist>
        </>
      ) : null}
      {posting ? <p className="mc-t3">{LIST_LABELS.postingNotBlocked}</p> : null}
    </Stack>
  );
}

// ---- the batched read --------------------------------------------------------------------------------------------

/** The ids to ask for: each once, in a stable order, so the same list is one cache entry. */
export function signalIds(ids: readonly (string | null | undefined)[]): string[] {
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id !== ""))].sort();
}

/** `items` in runs of at most `size` (one `requisition.signals` call each). */
export function chunk<T>(items: readonly T[], size: number = SIGNALS_BATCH): T[][] {
  if (size < 1) throw new Error("chunk: size must be at least 1");
  const out: T[][] = [];
  for (let at = 0; at < items.length; at += size) out.push(items.slice(at, at + size));
  return out;
}

type SignalsCall = (input: { requisition_ids: string[] }) => Promise<{ rows: SignalRow[] }>;

/** Every listed id's signal, ≤ 100 ids a call, the calls in parallel; an id the reader may not open is absent. */
export async function fetchSignals(ids: readonly string[], call: SignalsCall = requisitionSignals): Promise<SignalRow[]> {
  const batches = chunk(signalIds(ids));
  const answers = await Promise.all(batches.map((requisition_ids) => call({ requisition_ids })));
  return answers.flatMap((answer) => answer.rows);
}

export interface SignalsQuery {
  /** requisition id → its signal (only the ones the reader may open). */
  signals: Map<string, SignalRow>;
  /** The reader holds `requisition.signals` (the Interviewer does not): a list draws the column only then. */
  allowed: boolean;
  loading: boolean;
  error: Error | null;
}

/**
 * The signals of a list's requisitions in one batched read. Keyed under `requisitions`, so a requisition write that
 * refreshes the lists refreshes their signals too. No call without the tool in the reader's manifest (deny by default).
 */
export function useSignalsQuery(ids: readonly (string | null | undefined)[]): SignalsQuery {
  const { can } = useSession();
  const allowed = can("requisition.signals");
  const unique = signalIds(ids);
  const asked = allowed && unique.length > 0;
  const query = useRead(["requisitions", "signals", unique.join(",")], () => fetchSignals(unique), { enabled: asked });
  const signals = useMemo(() => new Map((query.data ?? []).map((row) => [row.requisition_id, row] as const)), [query.data]);
  return { signals, allowed, loading: asked && query.isLoading, error: query.error ?? null };
}

/** PARALLEL-MAP §2.1 S19: requisition id → signal for the ids of a list. */
export function useSignals(ids: readonly string[]): Map<string, SignalRow> {
  return useSignalsQuery(ids).signals;
}
